// Simple data service - reads from JSON files

export interface DataServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

class DataService {
  // Load data from JSON files
  private async fetchData<T>(endpoint: string): Promise<DataServiceResponse<T>> {
    try {
      const response = await fetch(`/data${endpoint}.json`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { 
        success: false, 
        error: `Failed to load ${endpoint}: ${error}` 
      };
    }
  }

  async getProfile() {
    return this.fetchData('/profile');
  }

  async getProjects() {
    return this.fetchData('/projects');
  }

  async getDashboards() {
    return this.fetchData('/dashboards');
  }

  async getWorkExperience() {
    return this.fetchData('/work-experience');
  }

  async getEducation() {
    return this.fetchData('/education');
  }

  async getSkills() {
    return this.fetchData('/skills');
  }

  async getProfileWithFallback() {
    const serverResult = await this.getProfile();
    if (serverResult.success && serverResult.data) {
      return serverResult;
    }
    
    return { success: false, error: serverResult.error || 'No profile data available' };
  }

  async getProjectsWithFallback() {
    const [projectsResult, dashboardsResult] = await Promise.all([
      this.getProjects(),
      this.getDashboards()
    ]);

    const allItems: any[] = [];

    if (projectsResult.success && projectsResult.data) {
      const data = projectsResult.data as any;
      if (Array.isArray(data)) {
        allItems.push(...data);
      } else if (Array.isArray(data.projects)) {
        allItems.push(...data.projects);
      }
    }

    if (dashboardsResult.success && dashboardsResult.data) {
      const data = dashboardsResult.data as any;
      if (Array.isArray(data)) {
        allItems.push(...data);
      } else if (Array.isArray(data.dashboards)) {
        allItems.push(...data.dashboards);
      }
    }

    if (allItems.length > 0) {
      return { success: true, data: allItems };
    }

    return {
      success: false,
      error: projectsResult.error || dashboardsResult.error || 'No project data available'
    };
  }

  async getExperiencesWithFallback() {
    const [workResult, educationResult] = await Promise.all([
      this.getWorkExperience(),
      this.getEducation()
    ]);

    const allItems: any[] = [];

    if (workResult.success && workResult.data) {
      const data = workResult.data as any;
      if (Array.isArray(data)) {
        allItems.push(...data);
      } else if (Array.isArray(data.workExperience)) {
        allItems.push(...data.workExperience);
      }
    }

    if (educationResult.success && educationResult.data) {
      const data = educationResult.data as any;
      if (Array.isArray(data)) {
        allItems.push(...data);
      } else if (Array.isArray(data.education)) {
        allItems.push(...data.education);
      }
    }

    if (allItems.length > 0) {
      return { success: true, data: allItems };
    }

    return {
      success: false,
      error: workResult.error || educationResult.error || 'No experience data available'
    };
  }

  async getSkillsWithFallback() {
    const serverResult = await this.getSkills();
    if (serverResult.success && serverResult.data) {
      return serverResult;
    }

    return { success: false, error: serverResult.error || 'No skills data available' };
  }
}

export const dataService = new DataService();
